/**
 * QUALITY-PHASE3 — Shared read-only detail view for a single quality record (premium side-panel layout, Sep 2026).
 * Client evidence stays READ-ONLY; the processor may only add Processor Response documents and comments.
 * All handlers, endpoints, 2 MB validation, accepted types and test ids are unchanged from the dialog version.
 */
import React, { useState } from 'react';
import { Loader2, Eye, Download, Upload, Paperclip, FileText, MessageSquare, ClipboardList, AlertOctagon, CheckCircle2, Send, CalendarDays, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTimeLocal } from '../../utils/formatters';
import { AnStatusPill } from '../analytics/ui/AnalyticsUi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

export const CATEGORY_LABELS = {
  data_error: 'Data Error', missing_info: 'Missing Info', document_issue: 'Document Issue',
  calculation_issue: 'Calculation Issue', wrong_jurisdiction: 'Wrong Jurisdiction',
};
export const SEVERITY_STYLES = { minor: 'bg-gray-100 text-gray-700', major: 'bg-orange-100 text-orange-800', critical: 'bg-red-100 text-red-800' };
export const STATUS_STYLES = {
  open: 'bg-amber-100 text-amber-800', under_review: 'bg-blue-100 text-blue-800',
  verified: 'bg-purple-100 text-purple-800', dismissed: 'bg-gray-200 text-gray-600',
  resolved: 'bg-emerald-100 text-emerald-800', returned_for_correction: 'bg-red-100 text-red-700',
};
export const recordBadge = (text, cls) => <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${cls || 'bg-gray-100 text-gray-600'}`}>{text}</span>;

export const SEVERITY_TONE = { minor: 'slate', major: 'orange', critical: 'red' };
export const STATUS_TONE = { open: 'amber', under_review: 'blue', verified: 'violet', dismissed: 'slate', resolved: 'green', returned_for_correction: 'red' };
export const statusLabel = (s) => ({ returned_for_correction: 'Returned', under_review: 'Under Review' }[s] || (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
export const SourcePill = ({ source }) => <AnStatusPill tone={source === 'internal_qc' ? 'blue' : 'violet'}>{source === 'internal_qc' ? 'Internal QC' : 'External Client'}</AnStatusPill>;

const fmtSize = (b) => (b == null ? '' : b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

const Section = ({ icon: Icon, title, count, action, tone = 'bg-blue-50 text-blue-600', children, testId }) => (
  <section className="qr-section rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_20px_-14px_rgba(15,23,42,.18)]" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${tone}`}><Icon className="h-3.5 w-3.5" /></span>
      <h4 className="text-[13px] font-semibold text-slate-900">{title}{count != null && <span className="text-slate-400 font-medium"> ({count})</span>}</h4>
      {action && <div className="ml-auto">{action}</div>}
    </div>
    {children}
  </section>
);

const Field = ({ label, children, testId }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 text-[12.5px]" data-testid={testId}>
    <span className="text-slate-500 shrink-0">{label}</span>
    <span className="text-slate-800 font-medium text-right min-w-0 break-words">{children}</span>
  </div>
);

const FileRow = ({ f, canFetch, onOpen }) => (
  <div className="an-hover flex items-center justify-between gap-2 rounded-xl border border-slate-200/80 bg-slate-50/60 px-3 py-2" data-testid={`my-attachment-row-${f.file_id}`}>
    <div className="flex items-center gap-2.5 min-w-0">
      <span className="h-8 w-8 shrink-0 rounded-lg bg-white border border-slate-200 text-blue-600 flex items-center justify-center"><FileText className="h-4 w-4" /></span>
      <div className="min-w-0">
        <div className="text-[12.5px] font-medium text-slate-800 truncate">{f.filename}</div>
        <div className="text-[11px] text-slate-500 truncate">{[fmtSize(f.size), f.uploaded_by_name, f.uploaded_at ? formatDateTimeLocal(f.uploaded_at) : null].filter(Boolean).join(' · ')}</div>
      </div>
    </div>
    {canFetch && (
      <div className="flex items-center gap-0.5 shrink-0">
        <button type="button" onClick={() => onOpen(f, 'view')} className="an-btn h-8 w-8 rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-700 flex items-center justify-center" title="View" data-testid={`my-view-${f.file_id}`}><Eye className="h-4 w-4" /></button>
        <button type="button" onClick={() => onOpen(f, 'download')} className="an-btn h-8 w-8 rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-700 flex items-center justify-center" title="Download" data-testid={`my-download-${f.file_id}`}><Download className="h-4 w-4" /></button>
      </div>
    )}
  </div>
);

export const QualityRecordDetail = ({ record, onRecordUpdate, onOpenOrder }) => {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);

  const openAttachment = async (fileEntry, mode) => {
    try {
      const res = await fetch(`${API}/quality/my-records/${record.id}/attachments/${fileEntry.file_id}/download?inline=${mode === 'view'}`, { headers: authHeaders() });
      if (!res.ok) { toast.error('Could not fetch file'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (mode === 'view') window.open(url, '_blank');
      else { const a = document.createElement('a'); a.href = url; a.download = fileEntry.filename; a.click(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not fetch file'); }
  };

  const uploadResponse = async (fileObj) => {
    if (!fileObj) return;
    if (fileObj.size > 2 * 1024 * 1024) { toast.error('File too large — maximum 2 MB per file'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', fileObj);
      const res = await fetch(`${API}/quality/my-records/${record.id}/response-attachments`, { method: 'POST', headers: authHeaders(), body: fd });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || 'Upload failed'); setBusy(false); return; }
      const data = await res.json();
      onRecordUpdate(data.record);
      toast.success(`${fileObj.name} uploaded`);
    } catch { toast.error('Upload failed'); }
    setBusy(false);
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/quality/my-records/${record.id}/response-comment`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment }),
      });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || 'Failed to add comment'); setBusy(false); return; }
      const data = await res.json();
      onRecordUpdate(data.record);
      setComment('');
      toast.success('Comment added');
    } catch { toast.error('Failed to add comment'); }
    setBusy(false);
  };

  const isExternal = record.source === 'external_client';
  const evidence = record.client_evidence_files || [];
  const responses = record.processor_response_files || [];
  const comments = record.processor_comments || [];

  return (
    <div className="space-y-3 text-sm min-w-0" data-testid="quality-record-detail">
      <div className="flex items-center gap-1.5 flex-wrap">
        <SourcePill source={record.source} />
        <AnStatusPill tone="slate">{CATEGORY_LABELS[record.category] || record.category || '—'}</AnStatusPill>
        {record.severity && <AnStatusPill tone={SEVERITY_TONE[record.severity] || 'slate'}>{record.severity.charAt(0).toUpperCase() + record.severity.slice(1)}</AnStatusPill>}
        <AnStatusPill tone={STATUS_TONE[record.status] || 'slate'} dot>{statusLabel(record.status)}</AnStatusPill>
      </div>
      {record.reported_at && (
        <div className="flex items-center gap-2 text-[12px] text-slate-500 pb-1 border-b border-slate-100" data-testid="my-record-reported">
          <CalendarDays className="h-3.5 w-3.5 text-slate-400" /><span>Reported on <span className="font-medium text-slate-700">{formatDateTimeLocal(record.reported_at)}</span></span>
        </div>
      )}

      <Section icon={ClipboardList} title="Order / Record Information" testId="my-record-order-info">
        <div className="divide-y divide-slate-100">
          <Field label="Order Number">
            {record.order_id && onOpenOrder
              ? <button type="button" onClick={() => onOpenOrder(record)} className="an-btn inline-flex items-center gap-1 text-blue-700 hover:text-blue-800 hover:underline" data-testid="detail-order-link">{record.order_number}<ExternalLink className="h-3 w-3" /></button>
              : record.order_number}
          </Field>
          {record.client_code && <Field label="Client">{record.client_code}</Field>}
          <Field label="Source">{isExternal ? 'External Client' : 'Internal QC'}</Field>
          <Field label="Category">{CATEGORY_LABELS[record.category] || record.category || '—'}</Field>
          {record.severity && <Field label="Severity"><AnStatusPill tone={SEVERITY_TONE[record.severity] || 'slate'} dot>{record.severity.charAt(0).toUpperCase() + record.severity.slice(1)}</AnStatusPill></Field>}
          {record.resolved_at && <Field label="Resolved on">{formatDateTimeLocal(record.resolved_at)}</Field>}
        </div>
      </Section>

      <Section icon={AlertOctagon} title="Issue Details" tone="bg-rose-50 text-rose-600" testId="my-record-issue">
        <div className="divide-y divide-slate-100">
          {record.error_type && <Field label="Error Type">{record.error_type}</Field>}
          {record.reported_by_name && <Field label={isExternal ? 'Reported by' : 'Returned by'}>{record.reported_by_name}{!isExternal && <span className="text-slate-400 font-normal"> (QC review)</span>}</Field>}
        </div>
        {record.description
          ? <div className="mt-2 rounded-xl bg-slate-50 border border-slate-100 p-3 text-[12.5px] text-slate-700 whitespace-pre-wrap break-words" data-testid="my-record-description">{record.description}</div>
          : !isExternal && <div className="mt-2 text-[12px] text-slate-500" data-testid="my-record-description">See the order&apos;s QC feedback for correction details.</div>}
      </Section>

      {isExternal && (
        <>
          <Section icon={Paperclip} title="Supporting Documents (Client Evidence)" count={evidence.length} tone="bg-indigo-50 text-indigo-600" testId="my-client-evidence-section"
            action={<span className="text-[11px] text-slate-400">read-only</span>}>
            {evidence.length === 0
              ? <div className="text-[12px] text-slate-400">No client evidence uploaded.</div>
              : <div className="space-y-1.5">{evidence.map(f => <FileRow key={f.file_id} f={f} canFetch onOpen={openAttachment} />)}</div>}
          </Section>

          <Section icon={Upload} title="My Response Documents" count={responses.length} tone="bg-emerald-50 text-emerald-600" testId="my-response-section"
            action={
              <label className="cursor-pointer">
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
                  onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadResponse(f); }} data-testid="my-response-upload-input" />
                <span className="an-btn inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50" data-testid="my-response-upload-btn">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
                </span>
              </label>
            }>
            <p className="text-[11.5px] text-slate-500 mb-2">Upload your explanation, corrected documents or email responses. PDF, JPG, PNG, DOCX, XLSX — max 2 MB each.</p>
            {responses.length > 0 && <div className="space-y-1.5 mb-2">{responses.map(f => <FileRow key={f.file_id} f={f} canFetch onOpen={openAttachment} />)}</div>}
            <label
              onDragOver={e => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) uploadResponse(f); }}
              className={`qr-drop flex items-center justify-center gap-2.5 rounded-xl border-2 border-dashed px-4 py-4 cursor-pointer text-[12px] ${dragging ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-slate-50/60 hover:border-blue-300 hover:bg-blue-50/40'}`} data-testid="my-response-dropzone">
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx" onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadResponse(f); }} />
              <span className="h-8 w-8 rounded-lg bg-white border border-slate-200 text-blue-600 flex items-center justify-center"><Upload className="h-4 w-4" /></span>
              <span className="text-slate-600">Drag &amp; drop files here <span className="text-blue-700 font-medium">or click to upload</span></span>
            </label>
          </Section>

          <Section icon={MessageSquare} title="Comments / Response" count={comments.length} tone="bg-violet-50 text-violet-600" testId="my-comments-section">
            {comments.length > 0 && (
              <div className="space-y-1.5 mb-3">
                {comments.map(c => (
                  <div key={c.id} className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2" data-testid={`my-comment-${c.id}`}>
                    <div className="text-[12.5px] text-slate-700 whitespace-pre-wrap">{c.comment}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{c.by_name} · {formatDateTimeLocal(c.at)}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input className="flex-1 h-9 px-3 text-[12.5px] rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="Add a comment about your response..." value={comment} onChange={e => setComment(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addComment(); }} data-testid="my-comment-input" />
              <button type="button" onClick={addComment} disabled={busy || !comment.trim()} className="an-btn an-btn-primary inline-flex items-center gap-1.5 h-9 rounded-xl px-3.5 text-[12.5px] font-semibold text-white bg-gradient-to-r from-blue-600 to-violet-600 disabled:opacity-40 shadow-sm" data-testid="my-comment-submit-btn">
                <Send className="h-3.5 w-3.5" /> Post
              </button>
            </div>
          </Section>

          {record.resolution_notes && (
            <Section icon={CheckCircle2} title="Resolution" tone="bg-emerald-50 text-emerald-600" testId="my-record-resolution">
              <div className="rounded-xl bg-emerald-50/70 border border-emerald-100 p-3 text-[12.5px] text-emerald-900 whitespace-pre-wrap">{record.resolution_notes}</div>
              {record.resolved_at && <div className="text-[11px] text-emerald-700/70 mt-1.5">Resolved {formatDateTimeLocal(record.resolved_at)}</div>}
            </Section>
          )}
        </>
      )}
    </div>
  );
};

export default QualityRecordDetail;
